import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import EditDiscountLinkDialog from './EditDiscountLinkDialog';
import EventQuickLinks from './EventQuickLinks';
import PromotionalLinkWizard from './PromotionalLinkWizard';
import SavedLinkCard from './SavedLinkCard';
import {
  findLegacyPromoTicketIndex,
  getBasePriceForLegacyLink,
  inferDiscountPercent,
  legacyLinkHasDiscount,
  recalculatePromoPrice,
} from './linkEditUtils';
import {
  buildEventPageUrl,
  buildRegistrationUrl,
  loadReferralLinks,
  resolveLegacyLinkDisplay,
  saveReferralLinks,
  slugifyRef,
  type ReferralLinkDraft,
  type TicketFormSlice,
} from './paymentFormUtils';
import { useFormContext } from 'react-hook-form';
import { useBrand } from '../../../brands/BrandProvider';
import { useBrandRegistrationCopy } from '../../../brands/registrationCopy';

interface TicketLinksPanelProps {
  slug: string;
  tickets: TicketFormSlice[];
}

const TicketLinksPanel: React.FC<TicketLinksPanelProps> = ({ slug, tickets }) => {
  const { setValue } = useFormContext();
  const brand = useBrand();
  const t = useBrandRegistrationCopy();
  const [referralLinks, setReferralLinks] = useState<ReferralLinkDraft[]>([]);
  const [editLinkId, setEditLinkId] = useState<string | null>(null);

  useEffect(() => {
    setReferralLinks(slug ? loadReferralLinks(slug) : []);
  }, [slug]);

  const persistReferralLinks = (next: ReferralLinkDraft[]) => {
    setReferralLinks(next);
    if (slug) saveReferralLinks(slug, next);
  };

  const updateReferralLink = (id: string, patch: Partial<ReferralLinkDraft>) => {
    persistReferralLinks(
      referralLinks.map((link) => (link.id === id ? { ...link, ...patch } : link))
    );
  };

  const removeReferralLink = (id: string) => {
    persistReferralLinks(referralLinks.filter((link) => link.id !== id));
  };

  const resolveReferralUrl = (link: ReferralLinkDraft): string => {
    if (!slug || !link.refCode.trim()) return '';

    const ref = slugifyRef(link.refCode);
    if (link.ticketIndex != null) {
      const ticket = tickets[link.ticketIndex];
      const ticketId = ticket?._id;
      return buildRegistrationUrl(slug, { ticketId, ref, brandKey: brand.key });
    }
    return buildRegistrationUrl(slug, { ref, brandKey: brand.key });
  };

  const editingLink = editLinkId ? referralLinks.find((link) => link.id === editLinkId) : null;
  const editingHasDiscount = editingLink ? legacyLinkHasDiscount(editingLink, tickets) : false;
  const editingPromoIndex = editingLink ? findLegacyPromoTicketIndex(editingLink, tickets) : null;
  const editingBasePrice = editingLink ? getBasePriceForLegacyLink(editingLink, tickets) : null;
  const editingTicketPrice =
    editingPromoIndex != null ? Number(tickets[editingPromoIndex]?.price) : null;

  const handleSaveLink = (values: { label: string; refCode: string; discountPercent?: number }) => {
    if (!editingLink) return;
    const hasDiscount = legacyLinkHasDiscount(editingLink, tickets);

    if (hasDiscount && values.discountPercent != null) {
      const editingBasePrice = getBasePriceForLegacyLink(editingLink, tickets);
      if (editingBasePrice == null) return;

      const finalPrice = recalculatePromoPrice(editingBasePrice, values.discountPercent);
      const promoIndex = findLegacyPromoTicketIndex(editingLink, tickets);

      if (promoIndex != null) {
        setValue(`tickets.${promoIndex}.price`, finalPrice, { shouldDirty: true });
        if (editingLink.baseTicketName) {
          setValue(`tickets.${promoIndex}.name`, `${editingLink.baseTicketName} — ${values.label}`, {
            shouldDirty: true,
          });
        }
      }

      updateReferralLink(editingLink.id, {
        label: values.label,
        refCode: values.refCode,
        discountPercent: values.discountPercent,
        finalPrice,
        hasDiscount: true,
      });
    } else {
      updateReferralLink(editingLink.id, {
        label: values.label,
        refCode: values.refCode,
      });
    }

    setEditLinkId(null);
  };

  const handleDeleteLink = (id: string) => {
    const link = referralLinks.find((item) => item.id === id);
    if (!link) return;
    const promoIndex = findLegacyPromoTicketIndex(link, tickets);
    if (promoIndex != null) {
      setValue(
        'tickets',
        tickets.filter((_, index) => index !== promoIndex),
        { shouldDirty: true }
      );
    }
    removeReferralLink(id);
    if (editLinkId === id) setEditLinkId(null);
  };

  const confirmDeleteLink = (id: string) => {
    if (
      window.confirm(
        t('Excluir este link? O link deixará de funcionar. Inscrições já feitas não são afetadas.')
      )
    ) {
      handleDeleteLink(id);
    }
  };

  if (!slug) {
    return (
      <Stack spacing={2.5}>
        <Typography variant="body2" color="text.secondary">
          Crie links nomeados para WhatsApp, Instagram ou grupos. Marque desconto só se quiser preço diferente.
        </Typography>
        <PromotionalLinkWizard
          mode="legacy"
          slug={slug}
          onLinkCreated={() => setReferralLinks(loadReferralLinks(slug))}
        />
        <Alert severity="info">
          Informe o endereço (slug) do evento na etapa de informações básicas e salve para gerar os links copiáveis.
        </Alert>
      </Stack>
    );
  }

  return (
    <Stack spacing={2.5}>
      <Typography variant="body2" color="text.secondary">
        Crie links nomeados para WhatsApp, Instagram ou grupos. Marque desconto só se quiser preço diferente.
      </Typography>

      <PromotionalLinkWizard
        mode="legacy"
        slug={slug}
        onLinkCreated={() => setReferralLinks(loadReferralLinks(slug))}
      />

      <Box>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
          Seus links personalizados
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Links que você criou acima — com nome, preço e código de origem.
        </Typography>

        {referralLinks.length === 0 ? (
          <Paper variant="outlined" sx={{ p: 2.5, bgcolor: 'grey.50', textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Nenhum link ainda. Preencha o formulário acima e clique em &quot;Criar link&quot;.
            </Typography>
          </Paper>
        ) : (
          <Stack spacing={1.5}>
            {referralLinks.map((link) => (
              <SavedLinkCard
                key={link.id}
                label={link.label.trim() || 'Link'}
                url={resolveReferralUrl(link)}
                refCode={link.refCode}
                info={resolveLegacyLinkDisplay(link, tickets)}
                onEdit={() => setEditLinkId(link.id)}
                onDelete={() => confirmDeleteLink(link.id)}
              />
            ))}
          </Stack>
        )}
      </Box>

      <EventQuickLinks
        eventPageUrl={buildEventPageUrl(slug, brand.key)}
        registrationUrl={buildRegistrationUrl(slug, { brandKey: brand.key })}
      />

      <EditDiscountLinkDialog
        open={!!editingLink}
        onClose={() => setEditLinkId(null)}
        onSave={handleSaveLink}
        onDelete={editingLink ? () => handleDeleteLink(editingLink.id) : undefined}
        hasDiscount={editingHasDiscount}
        initialLabel={editingLink?.label || ''}
        initialRefCode={editingLink?.refCode || ''}
        initialDiscountPercent={
          editingLink?.discountPercent ??
          (editingBasePrice && (editingLink?.finalPrice ?? editingTicketPrice)
            ? inferDiscountPercent(editingBasePrice, editingLink?.finalPrice ?? editingTicketPrice ?? 0) ?? 10
            : 10)
        }
        baseTicketName={editingLink?.baseTicketName || 'Ingresso'}
        basePrice={editingBasePrice}
      />
    </Stack>
  );
};

export default TicketLinksPanel;
